// CSV 내보내기 순수 함수 — 열 구조 계산·행 조립·문서 조립(spec D3·D4·D5·D6·D9).
// 의존성은 csv 유틸과 templateForm 두 개로 고정한다 — src/conf.js 같은 window 의존 모듈을 끌고 오면
// node 환경 jest 가 깨진다(frontend/docs/wiki/domain/rules/testing.md).
import { CSV_BOM, CSV_LINE_BREAK, formatCsvRow } from 'utils/importExport/utils/csv'
import {
  getFieldAliases,
  getTemplateFormFields,
  normalizeHeader,
} from 'utils/importExport/ImportV2Modal/templateForm'

// 운영 측정(2026-10-12, 사용자별 'all' 범위): 사용자 정의 열 수 p99 KR 36·JP 11, 50 초과는 KR 172명 중 2명 — spec D9
export const CSV_MAX_CUSTOM_COLUMNS = 50

// 메모(rich_note)는 본문이 리치텍스트 HTML 이라 CSV 셀로 다루지 않는다. 대량 조회 응답에는 is_memo 가 없고
// 기존 isMemoRecord 의 아이콘 폴백은 오탐이 가능해 항목 타입으로 판별한다(spec D5)
export const isCsvExportableRecord = (record) =>
  !(record.items || []).some((item) => item.type === 'rich_note')

// processRecord 가 템플릿 id 를 정하는 규칙과 같다(bulkExport.js:281)
export const getTemplateId = (record) => record.template?.id || record.template_id || null

// Title 은 레코드 제목 열이고 files 는 CSV 로 표현할 수 없다
const getClassFields = (schema) =>
  getTemplateFormFields(schema).filter((field) => field.label !== 'Title')

// 별칭 동치류(spec D4): 항목 label 은 영문이 아니라 작성 시점 로케일의 번역으로 저장돼 같은 필드가
// `아이디`·`ID` 로 갈라져 있다. 이 내보내기에 올라온 모든 스키마에서 한 필드의 별칭(영문 label + 모든 로케일
// 번역)을 한 덩어리로 잇고, 덩어리 하나를 열 하나로 쓴다. 키가 문자열 자체를 기준으로 정해지므로 같은
// 문자열은 항상 같은 열이다 — 템플릿 필드 열은 저장 label 이 곧 열인 방식보다 늘 수 없다(사용자 정의 열은 종류가 달라
// 같은 label 도 별도 열이라 전체 열 수는 늘 수 있다).
// 레코드 자기 템플릿 스키마로만 조회하면 한국어 base label 템플릿(운영에 실재)과 영문 템플릿이 같은 문자열을
// 서로 다른 키로 가려 열이 늘어난다(운영 KR 8명 — 측정 2026-10-12).
export const buildLabelClasses = (schemas) => {
  const parent = new Map()
  const find = (text) => {
    if (!parent.has(text)) parent.set(text, text)
    let root = text
    while (parent.get(root) !== root) root = parent.get(root)
    let node = text
    while (parent.get(node) !== root) {
      const next = parent.get(node)
      parent.set(node, root)
      node = next
    }
    return root
  }
  const aliasesOf = (schema, field) => [...getFieldAliases(schema, field)]

  schemas.forEach((schema) => {
    getClassFields(schema).forEach((field) => {
      const [first, ...rest] = aliasesOf(schema, field)
      rest.forEach((alias) => parent.set(find(first), find(alias)))
    })
  })

  // 같은 덩어리에 속한 스키마 필드 중 스키마 순서 첫 번째의 번역 — 열 헤더 폴백
  const headerByRoot = new Map()
  schemas.forEach((schema) => {
    getClassFields(schema).forEach((field) => {
      const root = find(aliasesOf(schema, field)[0])
      if (!headerByRoot.has(root)) headerByRoot.set(root, field.labelLocalized)
    })
  })

  const keyOf = (label) => find(normalizeHeader(label))
  const headerOf = (label, ownSchema) => {
    const normalized = normalizeHeader(label)
    const ownField =
      ownSchema &&
      getClassFields(ownSchema).find((field) => getFieldAliases(ownSchema, field).has(normalized))
    if (ownField) return ownField.labelLocalized
    return headerByRoot.get(find(normalized)) ?? label
  }
  return { keyOf, headerOf }
}

// 열 키는 문자열 이어붙이기(`PIN#2`)가 아니라 구조 키라서 label 에 `#2` 가 그대로 들어 있어도 다른 열과
// 겹치지 않는다. 순번은 한 레코드 안에서 같은 (종류, 이름)이 나온 횟수다.
export const getRecordColumnKeys = (items = [], keyOf = normalizeHeader) => {
  const seen = new Map()
  return items
    .filter((item) => item.type !== 'files')
    .map((item) => {
      const kind = item.custom ? 'custom' : 'field'
      const name = item.custom ? normalizeHeader(item.label) : keyOf(item.label)
      const group = JSON.stringify([kind, name])
      const occurrence = (seen.get(group) || 0) + 1
      seen.set(group, occurrence)
      return { key: JSON.stringify([kind, name, occurrence]), item }
    })
}

export const groupRecordsByTemplate = (records) => {
  const groups = new Map()
  records.forEach((record) => {
    const templateId = getTemplateId(record)
    if (!groups.has(templateId)) groups.set(templateId, [])
    groups.get(templateId).push(record)
  })
  return [...groups.entries()].map(([templateId, groupRecords]) => ({
    templateId,
    records: groupRecords,
  }))
}

// 이미 쓴 헤더와 겹치면 (2), (3) … 중 집합에 없는 첫 번호를 붙인다 — 가져오기가 겹친 헤더를 차단하므로
// 접미사 결과가 다른 열의 헤더와 다시 겹치지 않아야 한다
const dedupeHeaders = (rawHeaders) => {
  const used = new Set()
  return rawHeaders.map((header) => {
    let candidate = header
    let n = 1
    while (used.has(normalizeHeader(candidate))) {
      n += 1
      candidate = `${header} (${n})`
    }
    used.add(normalizeHeader(candidate))
    return candidate
  })
}

export const buildCsvPlan = (
  records,
  {
    schemaByTemplateId = {},
    templateColumnHeader,
    extraColumnHeader,
    maxCustomColumns = CSV_MAX_CUSTOM_COLUMNS,
  },
) => {
  const groups = groupRecordsByTemplate(records)
  const sortedRecords = groups.flatMap((group) => group.records)
  const withTemplateColumn = groups.length >= 2
  const schemas = groups.map((group) => schemaByTemplateId[group.templateId]).filter(Boolean)
  const { keyOf, headerOf } = buildLabelClasses(schemas)
  // 행 조립이 열 계산과 같은 키 규칙을 쓰도록 계획이 함수를 들고 간다
  const getColumnKeys = (items) => getRecordColumnKeys(items, keyOf)

  const columns = []
  const known = new Set()
  const overflowKeys = new Set()
  let customColumnCount = 0
  sortedRecords.forEach((record) => {
    const ownSchema = schemaByTemplateId[getTemplateId(record)]
    getColumnKeys(record.items).forEach(({ key, item }) => {
      if (known.has(key) || overflowKeys.has(key)) return
      if (item.custom) {
        // label 이 비어 있으면 열 이름을 만들 수 없어 항상 기타 열로 보낸다
        if (!normalizeHeader(item.label) || customColumnCount >= maxCustomColumns) {
          overflowKeys.add(key)
          return
        }
        customColumnCount += 1
        columns.push({ key, header: item.label, isCustom: true })
      } else {
        columns.push({ key, header: headerOf(item.label, ownSchema), isCustom: false })
      }
      known.add(key)
    })
  })

  // 시스템 필드 열을 사용자 정의 열보다 앞에 둔다. 가져오기는 헤더를 필드에 이을 때 "처음 맞는 헤더"를 고르므로
  // (templateForm.js:39 buildHeaderColumnMap) 같은 이름·다른 로케일 별칭의 사용자 정의 열이 앞에 있으면 실제 필드 대신
  // 그 열이 이어진다. 열 개수는 그대로이고 순서만 바뀐다 — 헤더 접미사 ` (2)` 도 사용자 정의 열이 받는다
  const orderedColumns = [
    ...columns.filter((column) => !column.isCustom),
    ...columns.filter((column) => column.isCustom),
  ]

  const titleField = schemas[0]?.fields.find((field) => field.label === 'Title')
  const hasExtraColumn = overflowKeys.size > 0
  const headers = dedupeHeaders([
    titleField?.labelLocalized ?? 'Title',
    ...(withTemplateColumn ? [templateColumnHeader] : []),
    ...orderedColumns.map((column) => column.header),
    ...(hasExtraColumn ? [extraColumnHeader] : []),
  ])
  const offset = withTemplateColumn ? 2 : 1
  const columnIndexByKey = Object.fromEntries(
    orderedColumns.map((column, i) => [column.key, offset + i]),
  )
  return {
    headers,
    columnIndexByKey,
    withTemplateColumn,
    sortedRecords,
    hasExtraColumn,
    getColumnKeys,
  }
}

// 복호화에 실패한 항목은 암호문이 그대로 남아 있어(bulkExport.js:169-179) 셀에 쓰면 값처럼 보인다 — 빈 칸으로 쓴다
const getCellValue = (item) => {
  if (item.decryptFailed) return ''
  if (item.content == null) return ''
  return String(item.content)
}

export const buildCsvRow = (exportRecord, plan, templateDisplayName = '') => {
  const cells = new Array(plan.headers.length).fill('')
  cells[0] = exportRecord.title
  if (plan.withTemplateColumn) cells[1] = templateDisplayName
  const extraLines = []
  let hasDecryptFailure = false
  plan.getColumnKeys(exportRecord.items).forEach(({ key, item }) => {
    // 비워 둔 암호 필드(content '')는 복호화가 던지지만(pages/records/components/decryptGuard.js:2-4) 잃은 값이
    // 없으므로 실패로 세지 않는다. 진짜 실패는 암호문이 content 에 남아 비어 있지 않다(bulkExport.js:169-179)
    if (item.decryptFailed && item.content) hasDecryptFailure = true
    const value = getCellValue(item)
    const index = plan.columnIndexByKey[key]
    if (index !== undefined) {
      cells[index] = value
    } else {
      extraLines.push(normalizeHeader(item.label) ? `${item.label}: ${value}` : value)
    }
  })
  if (plan.hasExtraColumn) cells[plan.headers.length - 1] = extraLines.join('\n')
  return { cells, hasDecryptFailure }
}

export const formatCsvLine = (cells) => formatCsvRow(cells) + CSV_LINE_BREAK

export const getCsvHeaderBlock = (headers) => CSV_BOM + formatCsvLine(headers)

// 규칙 원본은 가져오기 카탈로그의 getDisplayName(templateCatalog.js:41-50, 모듈 비공개) — 로케일 정확 일치 →
// 2글자 접두 폴백 → 영문 title. 표기 방식을 새로 만들지 말라는 위키(template-display-names.md §2)를 따라 규칙만 복제한다
export const getTemplateDisplayName = (translations, locale, templateName) => {
  if (!templateName) return ''
  const matchedKey = Object.keys(translations || {}).find((key) =>
    key.startsWith(locale.slice(0, 2)),
  )
  const byLocale = translations?.[locale] ?? translations?.[matchedKey]
  return byLocale?.[templateName] || templateName
}
